import type { User, Session, AuthChangeEvent, ScriptModel, ScriptVersionModel, FeedbackFormModel, ContactModel } from "./types";

const TOKEN_STORAGE_KEY = "story_gauge_auth_token";
const USER_STORAGE_KEY = "story_gauge_auth_user";

type AuthListener = (event: AuthChangeEvent, session: Session | null) => void;
const listeners = new Set<AuthListener>();

const notifyListeners = (event: AuthChangeEvent, session: Session | null) => {
  listeners.forEach((listener) => {
    try {
      listener(event, session);
    } catch (e) {
      console.error("Error in auth listener:", e);
    }
  });
};

const getStoredToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

const setStoredAuth = (token: string | null, user: User | null) => {
  if (typeof window === "undefined") return;
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  if (user) {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_STORAGE_KEY);
  }
};

const getStoredUser = (): User | null => {
  if (typeof window === "undefined") return null;
  const userJson = localStorage.getItem(USER_STORAGE_KEY);
  if (!userJson) return null;
  try {
    return JSON.parse(userJson) as User;
  } catch {
    return null;
  }
};

// API Fetch helper with automatic Bearer token injection
async function apiRequest<T = unknown>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
  } = {}
): Promise<T> {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`/api${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401 && token) {
      setStoredAuth(null, null);
      notifyListeners("SIGNED_OUT", null);
    }
    const errorMsg = data?.error || `Request failed with status ${response.status}`;
    throw new Error(errorMsg);
  }

  return data as T;
}

export const auth = {
  async getSession(): Promise<{ data: { session: Session | null }; error: Error | null }> {
    const token = getStoredToken();
    const user = getStoredUser();

    if (!token || !user) {
      return { data: { session: null }, error: null };
    }

    const session: Session = {
      access_token: token,
      token_type: "bearer",
      user,
    };

    return { data: { session }, error: null };
  },

  async getUser(): Promise<{ data: { user: User | null }; error: Error | null }> {
    const user = getStoredUser();
    return { data: { user }, error: null };
  },

  async signUp({
    email,
    password,
    options,
  }: {
    email: string;
    password: string;
    options?: { data?: { name?: string }; emailRedirectTo?: string };
  }): Promise<{ data: { user: User | null; session: Session | null }; error: Error | null }> {
    try {
      const response = await apiRequest<{ user: User; session: Session }>("/auth/signup", {
        method: "POST",
        body: {
          email,
          password,
          name: options?.data?.name,
        },
      });

      setStoredAuth(response.session.access_token, response.user);
      notifyListeners("SIGNED_IN", response.session);

      return { data: response, error: null };
    } catch (err: unknown) {
      return {
        data: { user: null, session: null },
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  },

  async signInWithPassword({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<{ data: { user: User | null; session: Session | null }; error: Error | null }> {
    try {
      const response = await apiRequest<{ user: User; session: Session }>("/auth/signin", {
        method: "POST",
        body: { email, password },
      });

      setStoredAuth(response.session.access_token, response.user);
      notifyListeners("SIGNED_IN", response.session);

      return { data: response, error: null };
    } catch (err: unknown) {
      return {
        data: { user: null, session: null },
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  },

  async signOut(): Promise<{ error: Error | null }> {
    setStoredAuth(null, null);
    notifyListeners("SIGNED_OUT", null);
    return { error: null };
  },

  onAuthStateChange(callback: AuthListener): { data: { subscription: { unsubscribe: () => void } } } {
    listeners.add(callback);

    // Trigger initial session state
    const token = getStoredToken();
    const user = getStoredUser();
    if (token && user) {
      setTimeout(() => {
        callback("INITIAL_SESSION", {
          access_token: token,
          token_type: "bearer",
          user,
        });
      }, 0);
    }

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            listeners.delete(callback);
          },
        },
      },
    };
  },

  async resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string }
  ): Promise<{ error: Error | null }> {
    try {
      await apiRequest("/auth/reset-password", {
        method: "POST",
        body: { email, redirectTo: options?.redirectTo },
      });
      return { error: null };
    } catch (err: unknown) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  },

  async updateUser(
    attributes: { password?: string; data?: { name?: string } },
    options?: { token?: string }
  ): Promise<{ data: { user: User | null }; error: Error | null }> {
    try {
      const response = await apiRequest<{ user: User }>("/auth/update-user", {
        method: "POST",
        body: {
          password: attributes.password,
          name: attributes.data?.name,
          token: options?.token,
        },
      });

      if (response.user) {
        setStoredAuth(getStoredToken(), response.user);
        notifyListeners("USER_UPDATED", {
          access_token: getStoredToken() || "",
          token_type: "bearer",
          user: response.user,
        });
      }

      return { data: response, error: null };
    } catch (err: unknown) {
      return {
        data: { user: null },
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  },
};

export const api = {
  scripts: {
    list: () => apiRequest<ScriptModel[]>("/scripts"),
    create: (payload: {
      movieName: string;
      customMessage?: string;
      scriptUrl?: string;
      scriptUrlTh?: string;
      passwordProtected?: boolean;
      reviewPassword?: string;
    }) => apiRequest<{ script: { id: string } }>("/scripts", { method: "POST", body: payload }),
    delete: (scriptId: string) => apiRequest<{ success: boolean }>(`/scripts/${scriptId}`, { method: "DELETE" }),
    addForm: (payload: { scriptId: string; formId: string; scriptVersionId: string; reviewLink?: string }) =>
      apiRequest<{ success: boolean; reviewLink: string }>(`/scripts/${payload.scriptId}/forms`, {
        method: "POST",
        body: payload,
      }),
    setCurrentVersion: (scriptId: string, versionId: string) =>
      apiRequest<{ success: boolean }>(`/scripts/${scriptId}/current-version`, {
        method: "PATCH",
        body: { versionId },
      }),
  },

  versions: {
    list: (scriptId: string) => apiRequest<ScriptVersionModel[]>(`/script-versions?scriptId=${scriptId}`),
    create: (payload: {
      scriptId: string;
      scriptUrl: string;
      scriptUrlTh?: string;
      versionName?: string;
      notes?: string;
      notesTh?: string;
      passwordProtected?: boolean;
      reviewPassword?: string;
    }) => apiRequest<ScriptVersionModel>("/script-versions", { method: "POST", body: payload }),
    update: (
      versionId: string,
      payload: {
        versionName?: string;
        scriptUrl?: string;
        notes?: string;
        notesTh?: string;
        passwordProtected?: boolean;
        reviewPassword?: string;
        isActive?: boolean;
      }
    ) => apiRequest<ScriptVersionModel>(`/script-versions/${versionId}`, { method: "PATCH", body: payload }),
  },

  forms: {
    list: () => apiRequest<FeedbackFormModel[]>("/forms"),
    get: (formId: string) => apiRequest<FeedbackFormModel & { questions: any[] }>(`/forms/${formId}`),
    save: (
      payload: {
        name: string;
        name_th?: string | null;
        description?: string | null;
        description_th?: string | null;
        questions: any[];
      },
      formId?: string | null
    ) =>
      formId
        ? apiRequest<{ success: boolean; formId: string }>(`/forms/${formId}`, { method: "PUT", body: payload })
        : apiRequest<{ success: boolean; formId: string }>("/forms", { method: "POST", body: payload }),
    delete: (formId: string) => apiRequest<{ success: boolean }>(`/forms/${formId}`, { method: "DELETE" }),
  },

  reviews: {
    getReviewData: (reviewLink: string) => apiRequest<any[]>(`/reviews/${reviewLink}`),
    verifyPassword: (password: string, hash?: string | null) =>
      apiRequest<{ isValid: boolean }>(`/reviews/unknown/verify-password`, {
        method: "POST",
        body: { password, hash },
      }),
    submitFeedback: (payload: {
      script_form_id: string;
      reviewer_name: string;
      reviewer_email: string;
      notes?: string | null;
      answers: Array<{ question_id: string; score: number }>;
    }) =>
      apiRequest<{ submissionId: string }>(`/reviews/${payload.script_form_id}/submit`, {
        method: "POST",
        body: payload,
      }),
    getAnalysis: (scriptFormId: string) => apiRequest<any[]>(`/scripts/analysis/${scriptFormId}`),
    deleteSubmission: (submissionId: string) =>
      apiRequest<{ success: boolean }>(`/submissions/${submissionId}`, { method: "DELETE" }),
  },

  contacts: {
    list: () => apiRequest<ContactModel[]>("/contacts"),
    upsert: (payload: { id?: string; name?: string | null; email: string }) =>
      apiRequest<ContactModel>("/contacts", { method: "POST", body: payload }),
    delete: (contactId: string) => apiRequest<{ success: boolean }>(`/contacts/${contactId}`, { method: "DELETE" }),
  },

  participants: {
    sync: (scriptFormId: string, participants: Array<{ contactId?: string; email: string; name?: string | null }>) =>
      apiRequest<any[]>("/participants/sync", {
        method: "POST",
        body: { scriptFormId, participants },
      }),
  },

  security: {
    log: (action: string, details: Record<string, unknown> = {}) =>
      apiRequest<{ success: boolean }>("/security/log", {
        method: "POST",
        body: { action, details, userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "" },
      }),
  },

  healthz: () => apiRequest<{ status: string; database: string; driver: string }>("/healthz"),
};

export const neon = {
  auth,
  api,
};
